import { useCallback, useEffect, useState } from 'preact/hooks';
import emitter from '../../core/events/eventemitter';

export type VideoRecorderState = 'none' | 'recording' | 'processing' | 'ready';

export interface VideoRecorderResult {
  state: VideoRecorderState;
  progress: number;
  videoUrl: string | null;
  error: string | null;
  startRecording: () => void;
  stopRecording: () => void;
  clear: () => void;
}

interface VideoRecorderOptions {
  maxDuration?: number; // in milliseconds, default 30000 (30 seconds)
  progressUpdateInterval?: number; // in milliseconds, default 100ms
  eventEmitter?: any; // Use the default emitter if not provided
  eventPrefix?: string; // Prefix for events, default 'video-recorder'
}

const defaultOptions: VideoRecorderOptions = {
  maxDuration: 30000, // 30 seconds
  progressUpdateInterval: 100, // 100ms
  eventPrefix: 'video-recorder',
};

/**
 * A hook for video recording functionality in 8thWall projects
 * 
 * This hook manages the video recording lifecycle including:
 * - Starting/stopping recording
 * - Tracking recording progress
 * - Processing recorded video
 * - Exposing the video URL for playback
 * 
 * @param options Configuration options
 * @returns VideoRecorderResult with state and methods
 */
export function useVideoRecorder(options: VideoRecorderOptions = {}): VideoRecorderResult {
  const { 
    maxDuration, 
    progressUpdateInterval, 
    eventEmitter, 
    eventPrefix 
  } = { ...defaultOptions, ...options };
  
  const [state, setState] = useState<VideoRecorderState>('none');
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(null);

  // Listen for events from the app
  useEffect(() => {
    const em = eventEmitter || emitter;
    const prefix = eventPrefix || 'video-recorder';
    
    const handleRecordingCompleted = (url: string) => {
      setVideoUrl(url);
      setState('ready');
    };
    
    const handleRecordingError = (errorMessage: string) => {
      setError(errorMessage);
      setState('none');
    };
    
    em.on(`${prefix}-completed`, handleRecordingCompleted);
    em.on(`${prefix}-error`, handleRecordingError);
    
    return () => {
      em.off(`${prefix}-completed`, handleRecordingCompleted);
      em.off(`${prefix}-error`, handleRecordingError);
    };
  }, [eventEmitter, eventPrefix]);

  // Update progress when recording
  useEffect(() => {
    let progressTimer: number | undefined;
    
    if (state === 'recording' && recordingStartTime !== null) {
      progressTimer = setInterval(() => {
        const elapsed = Date.now() - recordingStartTime;
        const newProgress = Math.min(elapsed / (maxDuration || 30000), 1);
        setProgress(newProgress);
        
        if (newProgress >= 1) {
          // Auto stop when max duration is reached
          stopRecording();
        }
      }, progressUpdateInterval || 100);
    }
    
    return () => {
      if (progressTimer) clearInterval(progressTimer);
    };
  }, [state, recordingStartTime, maxDuration, progressUpdateInterval]);

  // Start recording
  const startRecording = useCallback(() => {
    if (state !== 'none') return;
    
    const em = eventEmitter || emitter;
    const prefix = eventPrefix || 'video-recorder';
    
    setState('recording');
    setProgress(0);
    setRecordingStartTime(Date.now());
    em.emit(`${prefix}-start`);
  }, [state, eventEmitter, eventPrefix]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (state !== 'recording') return;
    
    const em = eventEmitter || emitter;
    const prefix = eventPrefix || 'video-recorder';
    
    setState('processing');
    setRecordingStartTime(null);
    em.emit(`${prefix}-stop`);
  }, [state, eventEmitter, eventPrefix]);

  // Clear the recorded video
  const clear = useCallback(() => {
    if (state !== 'ready') return;
    
    setVideoUrl(null);
    setState('none');
  }, [state]);

  return {
    state,
    progress,
    videoUrl,
    error,
    startRecording,
    stopRecording,
    clear,
  };
}